import { X } from 'lucide-react';
import { useEffect } from 'react';
import { Form, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { CategoryFormProps } from '@/types/category';

import { Button } from '@/components/ui/button';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ADD } from '@/constants';
import { useCategoryFormMutations } from '@/hooks/api/use-category-mutations';

export function CategoryForm({
  mode,
  initialName = '',
  onSuccess,
  onCancel,
  predefinedMessageCategoryId,
}: CategoryFormProps) {
  const { t } = useTranslation();

  const {
    register,
    reset,
    control,
    formState: {
      errors: { name },
      isValid,
    },
  } = useForm<{ name: string }>({
    defaultValues: { name: initialName },
    mode: 'onChange',
  });

  const { onValidSubmit, isSaving } = useCategoryFormMutations({
    mode,
    predefinedMessageCategoryId,
    onSuccess,
  });

  const title =
    mode === ADD
      ? t('common:configuration.predefinedMessages.categoryForm.addTitle')
      : t('common:configuration.predefinedMessages.categoryForm.editTitle');

  useEffect(() => {
    reset({ name: initialName });
  }, [initialName, reset]);

  return (
    <div className="bg-train-bg border-separator relative mb-6 rounded-lg border p-5 shadow-xs">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-3 right-3"
        onClick={onCancel}
      >
        <X className="h-4 w-4" />
      </Button>
      <h3 className="pr-10 text-lg font-semibold">{title}</h3>
      <Form
        control={control}
        className="mt-4 space-y-4"
        noValidate
        onSubmit={({ data }) => onValidSubmit(data)}
      >
        <Field data-invalid={Boolean(name)}>
          <FieldLabel htmlFor="category-form-name" className="gap-0.5 text-sm font-medium" required>
            {t('common:configuration.predefinedMessages.categoryForm.nameLabel')}
          </FieldLabel>
          <FieldContent>
            <Input
              id="category-form-name"
              placeholder={t(
                'common:configuration.predefinedMessages.categoryForm.namePlaceholder'
              )}
              aria-label={t('common:configuration.predefinedMessages.categoryForm.nameLabel')}
              aria-required
              aria-invalid={!!name}
              disabled={isSaving}
              {...register('name')}
            />
            {name && <FieldError className="text-xs">{name.message}</FieldError>}
          </FieldContent>
        </Field>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="submit" disabled={isSaving || !isValid}>
            {t('common:configuration.predefinedMessages.categoryForm.save')}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
            {t('common:configuration.predefinedMessages.categoryForm.cancel')}
          </Button>
        </div>
      </Form>
    </div>
  );
}
